import React, { useState } from 'react';
import ValidationResult from '@common/interfaces/api/validation-result';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextInput from '@common/components/Inputs/TextInput';
import { dateToStringFormat } from '@common/utils/date';
import { useTranslation } from 'react-i18next';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import {
  CopyIcon,
  CrossCloseIcon,
  EditIcon,
  HideInputIcon,
  ShowInputIcon,
} from '@common/utils/icons/icons';
import { selectIsUserAdminOrHigher } from '../../store/general/general-slice-selectors';
import { useAppSelector } from '../../store';

const RobotAuthenticationPasswordInput: React.FC<{
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  validationResult: ValidationResult;
  timestamp: string | null;
  label: string;
}> = ({ value, onBlur, onChange, validationResult, timestamp, label }) => {
  const { t } = useTranslation();

  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  const [isClearText, setIsClearText] = useState(false);
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);

  const copyToClipboardClickHandler = () => {
    navigator.clipboard.writeText(value);
  };

  const toggleClearTextClickHandler = () => {
    setIsClearText((prevState) => !prevState);
  };

  const toggleIsEditingEnabledClickHandler = () => {
    if (isAdmin) {
      setIsEditingEnabled((prevState) => !prevState);
    }
  };

  return (
    <Stack spacing={1}>
      <TextInput
        value={value}
        onChange={onChange}
        validationResult={validationResult}
        label={label}
        onBlur={onBlur}
        type={isClearText ? 'text' : 'password'}
        disabled={!isEditingEnabled}
        endAdornment={
          <Stack direction="row" mr={-2}>
            <TooltipIconButton
              tooltip={t('info-tooltip-toggle-visibility')}
              icon={
                isClearText ? (
                  <HideInputIcon data-testid="toggle-password-visibility" />
                ) : (
                  <ShowInputIcon data-testid="toggle-password-visibility" />
                )
              }
              onClick={toggleClearTextClickHandler}
            />
            <TooltipIconButton
              tooltip={t('info-tooltip-copy-to-clipboard')}
              icon={<CopyIcon data-testid="copy-to-clipboard-button" />}
              onClick={copyToClipboardClickHandler}
            />
            {isAdmin && (
              <TooltipIconButton
                tooltip={isEditingEnabled ? undefined : t('info-tooltip-edit')}
                icon={
                  isEditingEnabled ? (
                    <CrossCloseIcon data-testid="toggle-close-edit-input-button" />
                  ) : (
                    <EditIcon data-testid="toggle-edit-input-button" />
                  )
                }
                onClick={toggleIsEditingEnabledClickHandler}
              />
            )}
          </Stack>
        }
      />
      <Typography pl={2} variant="caption">
        {`${t('label-password-last-updated')}: ${timestamp != null ? dateToStringFormat(timestamp) : ''}`}
      </Typography>
    </Stack>
  );
};

export default RobotAuthenticationPasswordInput;
